/**
 * TreatX Admin Inquiries Page
 * Manages contact form submissions from the /contact page
 * P2.3b: Full i18n coverage
 * 
 * Features:
 * - View all inquiries with filtering by status
 * - Respond to inquiries
 * - Mark inquiries as resolved
 * - Assign inquiries to admin
 */
import React, { useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { 
  useContactInquiry, 
  ContactInquiryProvider,
  INQUIRY_STATUS,
  INQUIRY_CATEGORIES 
} from '../../context/ContactInquiryContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, LogOut, ArrowLeft, Search, Mail, Clock, CheckCircle,
  AlertCircle, MessageSquare, Send, X, User, Calendar, Filter, RefreshCw,
  Paperclip, Download
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';

// TREATX-INQUIRY-REPLY-DELIVERY-01 — one small badge per delivery channel.
const DELIVERY_TONE = {
  sent: 'bg-green-100 text-green-700 border-green-200',
  accepted: 'bg-green-100 text-green-700 border-green-200',
  not_applicable: 'bg-gray-100 text-gray-600 border-gray-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  not_configured: 'bg-amber-100 text-amber-800 border-amber-200',
  uncertain: 'bg-orange-100 text-orange-800 border-orange-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
};

const DELIVERY_WORD_FALLBACK = {
  sent: 'saved',
  accepted: 'accepted by provider',
  not_applicable: 'not applicable',
  pending: 'not sent yet',
  in_progress: 'sending…',
  not_configured: 'not configured',
  uncertain: 'unconfirmed — check before resending',
  failed: 'failed',
};
// Channels a plain Retry may re-attempt. `uncertain` is excluded on purpose:
// the provider may already have accepted it, so resending needs a decision.
const RETRYABLE = ['failed', 'not_configured', 'pending', 'in_progress'];

const DeliveryBadge = ({ label, channel, testId, word }) => {
  const status = channel?.status || 'unknown';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${DELIVERY_TONE[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
      title={channel?.reason || ''}
      data-testid={testId}
    >
      {label}: {word(status)}
    </span>
  );
};

// Inquiry Detail Modal
const InquiryDetailModal = ({ inquiry, onClose, onRespond, onResolve }) => {
  const { t } = useTranslation();
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Status word, translated, with the raw status as the last resort.
  const word = (status) => t(`treatxAdmin.inquiries.delivery.status.${status || 'unknown'}`, {
    defaultValue: DELIVERY_WORD_FALLBACK[status] || status || 'unknown',
  });
  const dt = (key, opts) => t(`treatxAdmin.inquiries.delivery.${key}`, opts);

  const downloadAttachment = async (att) => {
    setDownloading(att.attachmentId);
    try {
      const res = await api.get(
        `/admin/inquiries/${inquiry.inquiryId || inquiry.id}/attachments/${att.attachmentId}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName || att.attachmentId;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Error', description: 'Attachment could not be retrieved', variant: 'destructive' });
    } finally {
      setDownloading(null);
    }
  };
  
  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast({ title: 'Error', description: 'Please enter a response', variant: 'destructive' });
      return;
    }
    setSending(true);
    const result = await onRespond(inquiry.id, responseText);
    setSending(false);

    // An unknown outcome is NOT a failure and NOT a success: the reply may
    // be stored and even delivered. Keep the draft, say so plainly, and do
    // not invite a fresh Send that could duplicate it.
    if (result?.unknown) {
      toast({
        title: dt('unknownTitle'),
        description: dt('unknownBody'),
        variant: 'destructive',
      });
      return;
    }
    if (result?.rejected) {
      toast({
        title: dt('rejectedTitle'),
        description: dt('rejectedBody'),
        variant: 'destructive',
      });
      return;
    }

    const d = result?.delivery || {};
    const msg = d.messages?.status;
    const mail = d.email?.status;
    const emailOk = mail === 'accepted';
    const messagesOk = msg === 'sent' || msg === 'not_applicable';
    // The reply is confirmed persisted (the server answered, or we found the
    // record), so the draft can go.
    setResponseText('');
    if (emailOk && messagesOk) {
      toast({
        title: dt('deliveredTitle'),
        description: msg === 'sent' ? dt('deliveredBoth') : dt('deliveredEmailOnly'),
      });
    } else {
      toast({
        title: dt('incompleteTitle'),
        description: dt('incompleteBody', { messages: word(msg), email: word(mail) }),
        variant: 'destructive',
      });
    }
  };

  const handleRetry = async (responseId, resendUncertain = false) => {
    setRetrying(responseId);
    const result = await onRespond(inquiry.id, '', responseId, resendUncertain);
    setRetrying(null);
    if (result?.unknown) {
      toast({ title: dt('unknownTitle'), description: dt('unknownBody'), variant: 'destructive' });
      return;
    }
    const d = result?.delivery || {};
    toast({
      title: result?.success ? dt('retryDoneTitle') : dt('retryIncompleteTitle'),
      description: dt('retryBody', { messages: word(d.messages?.status), email: word(d.email?.status) }),
      variant: result?.success ? undefined : 'destructive',
    });
  };
  
  const getCategoryLabel = (categoryId) => {
    const cat = INQUIRY_CATEGORIES.find(c => c.value === categoryId);
    return cat ? cat.label : categoryId;
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case INQUIRY_STATUS.NEW:
        return <Badge className="bg-blue-500">New</Badge>;
      case INQUIRY_STATUS.IN_PROGRESS:
        return <Badge className="bg-amber-500">In Progress</Badge>;
      case INQUIRY_STATUS.RESOLVED:
        return <Badge className="bg-green-500">Resolved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {getStatusBadge(inquiry.status)}
                <span className="text-blue-200 text-sm">{getCategoryLabel(inquiry.category)}</span>
              </div>
              <h2 className="text-xl font-bold">{inquiry.subject}</h2>
              <p className="text-blue-200 text-sm mt-1">From: {inquiry.name} ({inquiry.email})</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Original Message */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Original Message
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border">
              <p className="text-gray-700 whitespace-pre-wrap">{inquiry.message}</p>
              <p className="text-xs text-gray-400 mt-3">
                Submitted: {formatDateTimeLocale(inquiry.submittedAt)}
              </p>
            </div>
          </div>

          {/* TREATX-DATA-PROTECTION-REQUEST-01 — attachments submitted with the
              inquiry. Bytes are streamed through the authorised admin endpoint;
              no public or presigned storage URL is ever exposed. */}
          {inquiry.attachments && inquiry.attachments.length > 0 && (
            <div className="mb-6" data-testid="inquiry-attachments">
              <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({inquiry.attachments.length})
              </h3>
              <ul className="space-y-2">
                {inquiry.attachments.map((att) => (
                  <li
                    key={att.attachmentId}
                    className="flex items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2"
                    data-testid={`inquiry-attachment-${att.attachmentId}`}
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{att.fileName}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {((att.sizeBytes || 0) / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadAttachment(att)}
                      disabled={downloading === att.attachmentId}
                      data-testid={`inquiry-attachment-download-${att.attachmentId}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      {downloading === att.attachmentId ? 'Opening…' : 'Download'}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Responses */}
          {inquiry.responses && inquiry.responses.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Responses ({inquiry.responses.length})
              </h3>
              <div className="space-y-3">
                {inquiry.responses.map((resp, idx) => (
                  <div key={resp.id || idx} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-gray-700 whitespace-pre-wrap">{resp.text}</p>
                    <p className="text-xs text-blue-600 mt-2">
                      By: {resp.by} | {formatDateTimeLocale(resp.timestamp)}
                    </p>
                    {/* TREATX-INQUIRY-REPLY-DELIVERY-01 — truthful per-channel
                        delivery state. A failed channel is visible here, so the
                        admin never has to read backend logs to find out. */}
                    {resp.delivery && (
                      <div
                        className="mt-3 flex flex-wrap items-center gap-2"
                        data-testid={`response-delivery-${resp.id}`}
                      >
                        <DeliveryBadge
                          label={dt('channelMessages')}
                          channel={resp.delivery.messages}
                          testId={`delivery-messages-${resp.id}`}
                          word={word}
                        />
                        <DeliveryBadge
                          label={dt('channelEmail')}
                          channel={resp.delivery.email}
                          testId={`delivery-email-${resp.id}`}
                          word={word}
                        />
                        {(RETRYABLE.includes(resp.delivery.messages?.status)
                          || RETRYABLE.includes(resp.delivery.email?.status)) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetry(resp.id)}
                            disabled={retrying === resp.id}
                            data-testid={`retry-delivery-${resp.id}`}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            {retrying === resp.id ? dt('retrying') : dt('retry')}
                          </Button>
                        )}
                        {resp.delivery.email?.status === 'uncertain' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-700 border-orange-300 hover:bg-orange-50"
                            onClick={() => handleRetry(resp.id, true)}
                            disabled={retrying === resp.id}
                            data-testid={`resend-uncertain-${resp.id}`}
                            title={dt('resendUncertainHint')}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            {dt('resendUncertain')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Add Response */}
          {inquiry.status !== INQUIRY_STATUS.RESOLVED && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                <Send className="h-4 w-4" />
                Add Response
              </h3>
              <Textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Type your response here..."
                rows={4}
                className="mb-3"
                data-testid="inquiry-response-input"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={handleSendResponse}
                  disabled={sending || !responseText.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="inquiry-send-response-btn"
                >
                  {sending ? 'Sending...' : 'Send Response'}
                  <Send className="h-4 w-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => onResolve(inquiry.id)}
                  className="text-green-600 border-green-600 hover:bg-green-50"
                  data-testid="inquiry-mark-resolved-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Resolved
                </Button>
              </div>
            </div>
          )}
          
          {/* Resolved Notice */}
          {inquiry.status === INQUIRY_STATUS.RESOLVED && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-medium">This inquiry has been resolved</p>
              {inquiry.resolvedAt && (
                <p className="text-sm text-green-600">
                  Resolved on {formatDateTimeLocale(inquiry.resolvedAt)}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t p-4 flex justify-end bg-gray-50">
          <Button variant="outline" onClick={onClose} data-testid="inquiry-modal-close-btn">Close</Button>
        </div>
      </div>
    </div>
  );
};

const TreatXAdminInquiriesContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, logout } = useTreatXAdminAuth();
  const { 
    getAllInquiries, 
    getInquiriesByStatus, 
    getStats,
    addResponse,
    resolveInquiry,
    updateInquiryStatus
  } = useContactInquiry();
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  // Keep only the ID in state and derive the row from the live list each
  // render — a snapshot went stale the moment a reply was delivered and the
  // list refetched, so the new response row and its delivery badges only
  // appeared after closing and reopening the modal.
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);
  
  // Redirect if not logged in - use useEffect to avoid calling navigate during render
  React.useEffect(() => {
    if (!admin) {
      navigate('/treatx-admin/login');
    }
  }, [admin, navigate]);
  
  // Show nothing while redirecting
  if (!admin) {
    return null;
  }
  
  const stats = getStats();
  const allInquiries = getAllInquiries();
  const selectedInquiry = selectedInquiryId
    ? allInquiries.find(i => i.id === selectedInquiryId) || null
    : null;
  
  // Filter and sort inquiries
  const filteredInquiries = allInquiries
    .filter(inq => {
      const matchesStatus = activeFilter === 'all' || inq.status === activeFilter;
      const matchesCategory = categoryFilter === 'all' || inq.category === categoryFilter;
      const matchesSearch = !searchQuery || 
        inq.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
  
  const handleLogout = () => {
    logout();
    navigate('/treatx-admin/login');
  };
  
  const getCategoryLabel = (categoryId) => {
    const cat = INQUIRY_CATEGORIES.find(c => c.value === categoryId);
    return cat ? cat.label : categoryId;
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case INQUIRY_STATUS.NEW:
        return <Badge className="bg-blue-500">New</Badge>;
      case INQUIRY_STATUS.IN_PROGRESS:
        return <Badge className="bg-amber-500">In Progress</Badge>;
      case INQUIRY_STATUS.RESOLVED:
        return <Badge className="bg-green-500">Resolved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 to-cyan-700 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/treatx-admin/dashboard')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="inquiries-title">Contact Inquiries</h1>
                <p className="text-sm text-blue-200">Manage messages from the contact form</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src={admin.avatar} 
                  alt={admin.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{admin.name}</p>
                  <p className="text-xs text-blue-200">{admin.title}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-blue-200 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="grid grid-cols-4 gap-4">
            <div 
              className={`text-center cursor-pointer rounded p-2 transition-colors ${activeFilter === 'all' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              onClick={() => setActiveFilter('all')}
              data-testid="stat-total"
            >
              <p className="text-xl font-bold text-slate-600">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div 
              className={`text-center cursor-pointer rounded p-2 transition-colors ${activeFilter === INQUIRY_STATUS.NEW ? 'bg-blue-100' : 'hover:bg-slate-50'}`}
              onClick={() => setActiveFilter(INQUIRY_STATUS.NEW)}
              data-testid="stat-new"
            >
              <p className="text-xl font-bold text-blue-600">{stats.new}</p>
              <p className="text-xs text-slate-500">New</p>
            </div>
            <div 
              className={`text-center cursor-pointer rounded p-2 transition-colors ${activeFilter === INQUIRY_STATUS.IN_PROGRESS ? 'bg-amber-100' : 'hover:bg-slate-50'}`}
              onClick={() => setActiveFilter(INQUIRY_STATUS.IN_PROGRESS)}
              data-testid="stat-in-progress"
            >
              <p className="text-xl font-bold text-amber-600">{stats.inProgress}</p>
              <p className="text-xs text-slate-500">In Progress</p>
            </div>
            <div 
              className={`text-center cursor-pointer rounded p-2 transition-colors ${activeFilter === INQUIRY_STATUS.RESOLVED ? 'bg-green-100' : 'hover:bg-slate-50'}`}
              onClick={() => setActiveFilter(INQUIRY_STATUS.RESOLVED)}
              data-testid="stat-resolved"
            >
              <p className="text-xl font-bold text-green-600">{stats.resolved}</p>
              <p className="text-xs text-slate-500">Resolved</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Inquiries
                {activeFilter !== 'all' && (
                  <Badge variant="outline" className="ml-2">
                    {activeFilter === INQUIRY_STATUS.NEW && 'New'}
                    {activeFilter === INQUIRY_STATUS.IN_PROGRESS && 'In Progress'}
                    {activeFilter === INQUIRY_STATUS.RESOLVED && 'Resolved'}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search inquiries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full sm:w-52"
                    data-testid="inquiry-search"
                  />
                </div>
                {/* P1-4: Category filter */}
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border text-sm bg-white"
                  data-testid="inquiry-category-filter"
                >
                  <option value="all">All Types</option>
                  {INQUIRY_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {/* P1-4: Status filter */}
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border text-sm bg-white"
                  data-testid="inquiry-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value={INQUIRY_STATUS.NEW}>New</option>
                  <option value={INQUIRY_STATUS.IN_PROGRESS}>In Progress</option>
                  <option value={INQUIRY_STATUS.RESOLVED}>Resolved</option>
                </select>
                {/* P1-4: Sort toggle */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')}
                  title={`Sort by ${sortOrder === 'newest' ? 'oldest' : 'newest'} first`}
                  data-testid="inquiry-sort-btn"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => { setActiveFilter('all'); setCategoryFilter('all'); setSearchQuery(''); setSortOrder('newest'); }}
                  title="Reset filters"
                  data-testid="inquiry-reset-filters"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredInquiries.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-600">No Inquiries Found</h3>
                <p className="text-slate-500">
                  {searchQuery ? 'Try adjusting your search criteria' : 'No contact form submissions yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredInquiries.map(inquiry => (
                  <div 
                    key={inquiry.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedInquiryId(inquiry.id)}
                    data-testid={`inquiry-${inquiry.id}`}
                  >
                    <div className="flex items-center gap-2 flex-shrink-0 w-auto sm:w-40">
                      {getStatusBadge(inquiry.status)}
                      <Badge variant="outline" className="hidden sm:inline-flex text-[10px]">
                        {getCategoryLabel(inquiry.category)}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900 truncate">{inquiry.subject}</h3>
                        {inquiry.responses?.length > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 flex-shrink-0">
                            {inquiry.responses.length} reply
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{inquiry.message}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                      <span className="flex items-center gap-1 w-28 truncate">
                        <User className="h-3 w-3 flex-shrink-0" />
                        {inquiry.name}
                      </span>
                      <span className="flex items-center gap-1 w-24">
                        <Clock className="h-3 w-3 flex-shrink-0" />
                        {formatTimeAgo(inquiry.submittedAt)}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" className="flex-shrink-0 text-xs h-7">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Detail Modal */}
      {selectedInquiry && (
        <InquiryDetailModal
          inquiry={selectedInquiry}
          onClose={() => setSelectedInquiryId(null)}
          onRespond={async (id, text, retryResponseId = null, resendUncertain = false) =>
            addResponse(id, text, admin?.name || 'TreatX Admin', retryResponseId, resendUncertain)}
          onResolve={async (id) => {
            await resolveInquiry(id, admin?.name || 'TreatX Admin');
            toast({ title: 'Inquiry Resolved', description: 'This inquiry has been marked as resolved.' });
          }}
        />
      )}
    </div>
  );
};

// Wrapper with provider
const TreatXAdminInquiries = () => {
  return (
    <ContactInquiryProvider>
      <TreatXAdminInquiriesContent />
    </ContactInquiryProvider>
  );
};

export default TreatXAdminInquiries;
